import { FC, ReactNode, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { ROOT_ELEMENT_ID } from '@lib/tools/shared/helpers/consts';

import { TJustify } from '@components/web/src/foundations/Flexbox/Flexbox';
import * as S from '@components/web/src/templates/Modals/ModalWrapper/styles';

interface IProps {
  children: ReactNode;
  isOpen?: boolean;
  modalContentWidth?: string;
  modalContentHeight?: string;
  minModalContentHeight?: string;
  maxModalContentHeight?: string;
  isTransparent?: boolean;
  withoutDefaultPadding?: boolean;
  justifyModalContent?: TJustify;
  isAnimated?: boolean;
  noScroll?: boolean;
  onClose?: () => void;
}

export const ModalWrapper: FC<IProps> = ({
  children,
  isOpen,
  modalContentWidth = '100%',
  modalContentHeight = 'fit-content',
  minModalContentHeight = 'auto',
  maxModalContentHeight = '100%',
  isTransparent = true,
  isAnimated = true,
  noScroll = false,
  withoutDefaultPadding = false,
  justifyModalContent = 'flex-start',
  onClose,
}) => {
  const modalContentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const rootElement = document.querySelector('#root') as HTMLDivElement;
    if (isOpen && rootElement) {
      rootElement.style.overflowY = 'hidden';
      rootElement.style.height = '100svh';
    }

    return () => {
      if (rootElement) {
        rootElement.style.overflowY = 'unset';
        rootElement.style.height = '100%';
      }
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }
  }, [children]);

  if (!isOpen) return null;
  const modalRootElement =
    document.getElementById('storybook-root') ||
    document.getElementById(ROOT_ELEMENT_ID) ||
    document.getElementById('root');

  return ReactDOM.createPortal(
    <S.ModalOverlay
      $isAnimated={isAnimated}
      $withoutDefaultPadding={withoutDefaultPadding}
      align="center"
      aria-modal="true"
      justify="center"
      role="dialog"
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        onClose?.();
      }}
    >
      <S.ModalContent
        ref={modalContentRef}
        $isTransparent={isTransparent}
        $maxModalContentHeight={maxModalContentHeight}
        $minModalContentHeight={minModalContentHeight}
        $modalContentHeight={modalContentHeight}
        $modalContentWidth={modalContentWidth}
        $noScroll={noScroll}
        direction="column"
        gap={0}
        justify={justifyModalContent}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </S.ModalContent>
    </S.ModalOverlay>,
    modalRootElement,
  );
};
